import React from 'react';
import {
  Descriptions,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Divider,
  Space
} from 'antd';
import {
  CalendarOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { JobPosting } from '../../types/recruitment';

const { Title, Text, Paragraph } = Typography;

interface JobPostingDetailsProps {
  job: JobPosting;
}

const JobPostingDetails: React.FC<JobPostingDetailsProps> = ({ job }) => {
  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return num > 0 ? `₦${num.toLocaleString()}` : 'Not specified';
  };

  const formatDate = (date: string) => {
    return date ? new Date(date).toLocaleDateString() : 'Not specified';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'draft': return 'orange';
      case 'closed': return 'red';
      default: return 'default';
    }
  };

  return (
    <div>
      {/* Header Section */}
      <Card style={{ marginBottom: '16px' }}>
        <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
          {job.title}
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          {job.department} • {job.location}
        </Text>
        <br />
        <Space style={{ marginTop: '8px' }}>
          <Tag color="blue">{job.job_id}</Tag>
          <Tag color={getStatusColor(job.status)}>
            {job.status.toUpperCase()}
          </Tag>
          <Tag color="green">
            {job.employment_type === 'full_time' ? 'Full Time' : job.employment_type.replace('_', ' ').toUpperCase()}
          </Tag>
        </Space>
      </Card>

      {/* Job Details */}
      <Card title="Job Information" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Job ID">
            <Text code>{job.job_id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(job.status)}>
              {job.status.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label="Department">
            <Space>
              <TeamOutlined style={{ color: '#1890ff' }} />
              {job.department}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Location">
            <Space>
              <EnvironmentOutlined style={{ color: '#52c41a' }} />
              {job.location}
            </Space>
          </Descriptions.Item>
          
          <Descriptions.Item label="Positions Available">
            <Space>
              <TeamOutlined />
              {job.positions_available}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Employment Type">
            {job.employment_type === 'full_time' ? 'Full Time' : job.employment_type.replace('_', ' ').toUpperCase()}
          </Descriptions.Item>
          
          <Descriptions.Item label="Salary Range" span={2}>
            <Space>
              <DollarOutlined style={{ color: '#faad14' }} />
              {formatCurrency(job.salary_min)} - {formatCurrency(job.salary_max)}
            </Space>
          </Descriptions.Item>
          
          <Descriptions.Item label="Created Date">
            <Space>
              <CalendarOutlined />
              {formatDate(job.created_at)}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            <Space>
              <CalendarOutlined />
              {formatDate(job.updated_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Job Description */}
      {job.description && (
        <Card title="Job Description" style={{ marginBottom: '16px' }}>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
            {job.description || 'No description provided.'}
          </Paragraph>
        </Card>
      )}

      {/* Requirements */}
      {job.requirements && (
        <Card title="Requirements">
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
            {job.requirements || 'No specific requirements listed.'}
          </Paragraph>
        </Card>
      )}

      <Divider />
      <Text type="secondary" style={{ fontSize: '12px' }}>
        Job ID: {job.job_id} | Created: {formatDate(job.created_at)}
      </Text>
    </div>
  );
};

export default JobPostingDetails;
